import type { ReactNode } from "react";
import { ActionBar, ActionBarJumps } from "@/components/patterns/ActionBar";
import type { ActionBarState } from "@/components/patterns/ActionBar";
import { openFoldsAround } from "@/components/ui/Disclosure";

/**
 * One condition on leaving a page, as the server projects it: whether it
 * stands in the way, the first item it is waiting on, and the words for it.
 * Both gates the server serves have this shape.
 */
export interface FooterCondition {
  id: string;
  outstanding: boolean;
  first_id: string | null;
  label: string;
}

interface StageFooterProps {
  /** The bold line: what is missing, or that the stage is ready. */
  heading: string;
  detail: string;
  ready: boolean;
  /** The attorney has confirmed the page; the action now carries them on. */
  settled?: boolean;
  submitting?: boolean;
  onSubmit: () => void;
  /** The button's wording, and what it says while a submission is in flight. */
  label: string;
  pendingLabel: string;
  testId: string;
  /** Under the detail line: what a stage says beyond its conditions, or nothing. */
  children?: ReactNode;
  /** Pinned to the foot of the content column, for a bar the design keeps in view. */
  sticky?: boolean;
  /** Drawn just above the bar and kept in view with it: the banner over the page's pending changes. */
  above?: ReactNode;
  /** Closed, the bar takes the brand's wash, as v0.28 draws Intake's. */
  wash?: boolean;
  /** Drawn inside the action, before its words. */
  actionIcon?: ReactNode;
  /** After the action's words: the way on. */
  actionTrail?: ReactNode;
  /** The conditions the gate lists; only the outstanding ones are drawn. */
  conditions?: readonly FooterCondition[];
  /** Where a condition's first item lives on the page, as an element id, or null when the page cannot place it. */
  resolveTarget?: (condition: FooterCondition) => string | null;
}

/**
 * Take the attorney to one element of the page: scrolled into view and, where
 * it can take focus, focused, so a keyboard reader lands there too. The one
 * way a footer's condition, a markup or a warning jumps to what it names.
 */
export function landOn(id: string): void {
  const target = document.getElementById(id);
  if (target === null) return;
  const arrive = () => {
    target.focus();
    target.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  // A target inside a folded panel cannot take focus until the fold has drawn open.
  if (openFoldsAround(target)) requestAnimationFrame(arrive);
  else arrive();
}

/**
 * The gate at the foot of every page that gates the next: intake, the
 * novelty tabs, Invention and Claim Strategy draw this and nothing else, so
 * they read as one bar with different words. The bar is the `ActionBar`
 * pattern; this is its stage-facing half, which turns the server's conditions
 * into the jumps listed under the sentence.
 *
 * There is deliberately no second control on the bar itself: the only way
 * past a closed gate is to meet it, and a disabled button names what is
 * missing rather than arguing. A caller may still gate its own onSubmit, as
 * the novelty review footer does with a warning before an out-of-date
 * analysis is carried forward; that is the caller's rule, not the bar's.
 */
export function StageFooter({
  heading,
  detail,
  ready,
  settled = false,
  submitting = false,
  onSubmit,
  label,
  pendingLabel,
  testId,
  children,
  sticky = false,
  above,
  wash = false,
  actionIcon,
  actionTrail,
  conditions = [],
  resolveTarget = () => null,
}: StageFooterProps) {
  const state: ActionBarState = settled
    ? "settled"
    : ready
      ? "ready"
      : "closed";
  const jumps = conditions
    .filter((c) => c.outstanding)
    .map((condition) => {
      const target = resolveTarget(condition);
      return {
        key: condition.id,
        label: condition.label,
        onJump:
          target === null
            ? undefined
            : () => {
                landOn(target);
              },
      };
    });
  return (
    <ActionBar
      testId={testId}
      heading={heading}
      detail={detail}
      state={state}
      actionLabel={label}
      actionIcon={actionIcon}
      actionTrail={actionTrail}
      busy={submitting}
      busyLabel={pendingLabel}
      disabled={!ready}
      onAction={onSubmit}
      sticky={sticky}
      above={above}
      wash={wash}
    >
      {/* What the caller says (a refusal, a warning) sits under the detail
          line, and the conditions under that, as the run footer drew them. */}
      {children}
      <ActionBarJumps jumps={jumps} />
    </ActionBar>
  );
}
